import { environmentService } from '../Environment'

class UserGuidingService {
  private static instance: UserGuidingService

  private static readonly USERGUIDING_ID = 'Q0Y10792036GID'

  private hasIdentified = false

  private scriptLoaded = false

  private constructor() {}

  public static getInstance(): UserGuidingService {
    if (!UserGuidingService.instance) {
      UserGuidingService.instance = new UserGuidingService()
    }
    return UserGuidingService.instance
  }

  /**
   * Dynamically load the UserGuiding script
   * Only loads in production environment (or when forceProduction query param is set)
   */
  public loadScript(): void {
    // Only run in production environment
    if (!environmentService.isProduction()) {
      console.debug(
        '[UserGuiding] Skipping script load - not in production environment'
      )
      return
    }

    // Prevent loading script multiple times
    if (this.scriptLoaded) {
      console.debug('[UserGuiding] Script already loaded')
      return
    }

    // Check if window is available
    if (typeof window === 'undefined') {
      console.warn('[UserGuiding] Window not available, cannot load script')
      return
    }

    try {
      // Inject the UserGuiding script using the same code from index.html
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(function (g: any, u: any, i: any, d: any, e: any, s: any) {
        g[e] = g[e] || []
        const f = u.getElementsByTagName(i)[0]
        const k = u.createElement(i)
        k.async = true
        k.src = `https://static.userguiding.com/media/user-guiding-${s}-embedded.js`
        f.parentNode.insertBefore(k, f)
        if (g[d]) return
        const ug: any = (g[d] = { q: [] as any[] })
        ug.c = function (n: any) {
          return function () {
            ug.q.push([n, arguments])
          }
        }
        const m = [
          'previewGuide',
          'finishPreview',
          'track',
          'identify',
          'hideChecklist',
          'launchChecklist',
        ]
        for (let j = 0; j < m.length; j += 1) {
          ug[m[j]] = ug.c(m[j])
        }
      })(
        window,
        document,
        'script',
        'userGuiding',
        'userGuidingLayer',
        UserGuidingService.USERGUIDING_ID
      )

      this.scriptLoaded = true
      console.debug('[UserGuiding] Script loaded successfully')
    } catch (error) {
      console.error('[UserGuiding] Failed to load script', error)
    }
  }

  /**
   * Check if UserGuiding script is loaded
   */
  private isLoaded(): boolean {
    return (
      typeof window !== 'undefined' && typeof window.userGuiding !== 'undefined'
    )
  }

  /**
   * Wait for UserGuiding script to be loaded
   */
  private async waitForLoad(
    maxAttempts = 50,
    interval = 100
  ): Promise<boolean> {
    for (let i = 0; i < maxAttempts; i++) {
      if (this.isLoaded()) {
        return true
      }
      await new Promise(resolve => setTimeout(resolve, interval))
    }
    return false
  }

  /**
   * Identify a user in UserGuiding
   * @param userEmail - User email
   */
  public async identify(userEmail: string): Promise<void> {
    try {
      // Only run in production environment
      if (!environmentService.isProduction()) {
        console.debug(
          '[UserGuiding] Skipping identify - not in production environment'
        )
        return
      }

      // Prevent multiple identifications
      if (this.hasIdentified) {
        console.warn('[UserGuiding] User already identified in this session')
        return
      }

      // Wait for script to load
      const loaded = await this.waitForLoad()
      if (!loaded) {
        console.warn('[UserGuiding] Script failed to load within timeout')
        return
      }

      // Call identify
      if (window.userGuiding?.identify) {
        window.userGuiding.identify(userEmail)
        this.hasIdentified = true
        console.debug('[UserGuiding] User identified successfully', {
          userEmail,
        })
      }
    } catch (error) {
      console.error('[UserGuiding] Failed to identify user', error)
    }
  }

  /**
   * Track a custom event in UserGuiding
   * @param eventName - Name of the event
   * @param properties - Optional event properties
   */
  public track(eventName: string, properties?: Record<string, any>): void {
    try {
      if (!this.isLoaded()) {
        console.warn('[UserGuiding] Script not loaded, cannot track event')
        return
      }

      if (window.userGuiding?.track) {
        window.userGuiding.track(eventName, properties)
      }
    } catch (error) {
      console.error('[UserGuiding] Failed to track event', error)
    }
  }

  /**
   * Reset identification state (call on logout)
   */
  public destroy(): void {
    this.hasIdentified = false
    console.log('[UserGuiding] Session destroyed')
  }
}

export const userGuidingService = UserGuidingService.getInstance()
